import React, { useRef, useState, useEffect } from 'react';
import { WizardStepProps } from '@components/Wizard/Wizard';
import PrimaryButton from '@components/PrimaryButton/PrimaryButton';
import RecoveryPhrase, { isFormComplete } from '@components/RecoveryPhrase/RecoveryPhrase';
import {
  Body,
  Title,
  Description,
  Separator,
  ButtonContainer,
  Spacer
} from '../LoginWallet.styles';
import { useTranslation } from 'react-i18next';
import { TonWalletUtils } from '@lib/secrets';
import LoadingModal from '@components/LoadingModal/LoadingModal';
import { IncompatibleWalletError, InvalidMnemonicError } from '@lib/error';
import { ModalWithIcon } from '@components/ModalWithIcon/ModalWithIcon';
import ReactDOM from 'react-dom';
import AlertBox from '@components/AlertBox/AlertBox';
import { getPlatform } from '@lib/telegram';

// Scroll constants for Android keyboard auto-scroll workaround
// Android's virtual keyboard triggers auto-scroll around 120-150ms after focus
const SCROLL_OFFSET = 55; // px - Amount to scroll down per input row
const SCROLL_ANIMATION_DURATION_IOS = 100; // ms - Smooth animation on iOS
const SCROLL_FORCE_START_DELAY = 50; // ms - Initial delay before forcing scroll
const SCROLL_FORCE_INTERVAL = 30; // ms - Interval between forced scroll updates
const SCROLL_FORCE_MAX_DURATION = 500; // ms - Maximum time to keep forcing scroll
const SCROLL_USER_TOLERANCE = 5; // px - Threshold to detect user-initiated scroll

interface RecoveryPhraseInputStepProps extends WizardStepProps {
  onWalletDataGenerated?: (data: {mnemonic: string, secretKey: Uint8Array}) => void;
}

const RecoveryPhraseInputStep: React.FC<RecoveryPhraseInputStepProps> = ({ onNext, onWalletDataGenerated }) => {
  const [phrases, setPhrases] = useState<string[]>(Array(12).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [modalDescription, setModalDescription] = useState('')
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs for scroll cleanup
  const scrollCleanupRef = useRef<(() => void) | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const handlePhrasesChange = (newPhrases: string[]) => {
    setPhrases(newPhrases);
  };

  const handleEnterNewLine = () => {
    if (!containerRef.current) return;

    // Clean up any existing scroll operation
    if (scrollCleanupRef.current) {
      scrollCleanupRef.current();
      scrollCleanupRef.current = null;
    }

    const isAndroid = getPlatform() === 'android';
    const container = containerRef.current;
    const startPosition = container.scrollTop;
    const targetPosition = startPosition + SCROLL_OFFSET;

    if (isAndroid) {
      // Android: Use interval-based forcing to override browser's auto-scroll
      // The browser's auto-scroll happens around 120-150ms after input focus
      let lastKnownScroll = startPosition;
      let intervalId: NodeJS.Timeout | null = null;
      let initialTimeoutId: NodeJS.Timeout | null = null;
      let stopTimeoutId: NodeJS.Timeout | null = null;

      const forceScroll = () => {
        if (!containerRef.current) return;

        const currentScroll = containerRef.current.scrollTop;

        // Detect user-initiated scroll: if scroll position changed by more than tolerance
        // and it's moving away from our target, stop forcing
        if (Math.abs(currentScroll - lastKnownScroll) > SCROLL_USER_TOLERANCE) {
          const movingAwayFromTarget = Math.abs(currentScroll - targetPosition) >
                                       Math.abs(lastKnownScroll - targetPosition);
          if (movingAwayFromTarget) {
            // User is scrolling - respect their input and stop forcing
            if (scrollCleanupRef.current) {
              scrollCleanupRef.current();
            }
            return;
          }
        }

        lastKnownScroll = targetPosition;
        containerRef.current.scrollTop = targetPosition;
      };

      // Start forcing scroll after initial delay
      initialTimeoutId = setTimeout(() => {
        forceScroll();
        // Continue forcing at regular intervals
        intervalId = setInterval(forceScroll, SCROLL_FORCE_INTERVAL);
      }, SCROLL_FORCE_START_DELAY);

      // Stop forcing after max duration
      stopTimeoutId = setTimeout(() => {
        if (scrollCleanupRef.current) {
          scrollCleanupRef.current();
        }
      }, SCROLL_FORCE_MAX_DURATION);

      // Cleanup function
      scrollCleanupRef.current = () => {
        if (initialTimeoutId) clearTimeout(initialTimeoutId);
        if (intervalId) clearInterval(intervalId);
        if (stopTimeoutId) clearTimeout(stopTimeoutId);
        scrollCleanupRef.current = null;
      };
    } else {
      // iOS: Use smooth requestAnimationFrame-based animation
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        if (!containerRef.current) {
          scrollCleanupRef.current = null;
          return;
        }

        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / SCROLL_ANIMATION_DURATION_IOS, 1);

        // Ease-out quad function for smooth deceleration
        const easeProgress = 1 - (1 - progress) * (1 - progress);

        containerRef.current.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

        if (progress < 1) {
          rafIdRef.current = requestAnimationFrame(animateScroll);
        } else {
          rafIdRef.current = null;
          scrollCleanupRef.current = null;
        }
      };

      rafIdRef.current = requestAnimationFrame(animateScroll);

      // Cleanup function for iOS animation
      scrollCleanupRef.current = () => {
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        scrollCleanupRef.current = null;
      };
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollCleanupRef.current) {
        scrollCleanupRef.current();
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (isFormComplete(phrases)) {
      setIsLoading(true);
      try {
        const words = phrases.map((phrase) => phrase.trim().toLowerCase()).join(' ')
        // Generate wallet data but don't store it yet
        const walletData = await TonWalletUtils.generateRecoveryWalletData(words);
        onWalletDataGenerated?.(walletData);
        setIsLoading(false);
        onNext?.();
      } catch (error) {
        setIsLoading(false);
        await TonWalletUtils.clear()
        await TonWalletUtils.clearToken()
        if (error instanceof InvalidMnemonicError) {
          setModalTitle(t('errors.2002.title'))
          setModalDescription(t('errors.2002.description'))
        } else if (error instanceof IncompatibleWalletError) {
          setModalTitle(t('errors.2000.title'))
          setModalDescription(t('errors.2000.description'))
        } else {
          setModalTitle(t('page.loginWallet.steps.recoveryPhrase.errorTitle'))
          setModalDescription(t('page.loginWallet.steps.recoveryPhrase.errorDescription'))
        }
        setShowModal(true)
      }
    }
  };
  
  return (
    <Body ref={containerRef}>
      <Description>
        {t('page.loginWallet.steps.recoveryPhrase.description')}
      </Description>

      {ReactDOM.createPortal(
          <ModalWithIcon 
              title={modalTitle}
              description={modalDescription}
              onPositive={() => setShowModal(false)}
              positiveText={'OK'}
              type="failed"
              visible={showModal}
              onModalOverlayClick={() => setShowModal(false)}
          />, 
        document.body)}

      <Title>{t('page.loginWallet.steps.recoveryPhrase.recoveryPhraseTitle')}</Title>
      
      <RecoveryPhrase 
        mode="input"
        phrases={phrases}
        onPhrasesChange={handlePhrasesChange}
        onEnterNewLine={handleEnterNewLine}
      />

      <AlertBox 
        alertText={t('page.loginWallet.steps.recoveryPhrase.compatibility')}
        enableIcon={false}
      />

      <Separator />

      <Spacer />

      <LoadingModal visible={isLoading} message={t('page.loginWallet.steps.recoveryPhrase.loading')} />
      
      <ButtonContainer>
        <PrimaryButton 
          disabled={!isFormComplete(phrases)}
          onClick={handleSubmit}
        >
          {t('page.loginWallet.steps.recoveryPhrase.buttonText')}
        </PrimaryButton>
      </ButtonContainer>
    </Body>
  );
};

export default RecoveryPhraseInputStep;
